import _ from 'lodash'
import { defaultLocale, languages } from '@/assets/internationalization/internationalizations.js'

/**
@CONSTRUCTOR: Store
@PARAMS: none
@DESC: Is used to keep track of data in the NGWI application
*/

export class Store {
  constructor() {
    this.storeColors = { // Used by setdata/getData for debugging
      update: 'background: #013e68;color: #fff;display: block;text-align: center',
      warn: 'background: #b76b00;color: #fff;display: block;text-align: center'
    }
    this.debug = false // Store change notifier (process.env.NODE_ENV === 'development')
    this.resetStoreData() // Init store
  }

  //-Helpers---------------------------------------------------------------------------
  /**
  @FUNC: _deepSameKeysRemoveDiff
  @PARAMS: Object o1, Object o2
  @DESC: Used to deep compare object keys and removes from o1 if it doesn't exist in o2 (WARNING RECURSIVE)
  */
  _deepSameKeysRemoveDiff(o1, o2) {
    // Get the keys of each object
    const o1keys = Object.keys(o1).sort()
    // Check any objects
    return o1keys.every(key => {
      let v1 = o1[key]
      const v2 = o2[key]
      const t1 = typeof v1
      const t2 = typeof v2
      if (t1 !== t2) {
        console.warn(`%cNo match for' ${key}, removing`, this.storeColors.warn)
        delete o1[key] //
        return false
      }
      return t1 === 'object' ? this._deepSameKeysRemoveDiff(v1, v2) : true
    })
  }
  //-Helpers end---------------------------------------------------------------------------

  /**
  @FUNC: setData
  @PARAMS: String Path, Var State
  @DESC: Is used to set data from store. It streamlines the process and creates
  a controllable entry point.
  */
  setData(path, state) {
    if(_.get(this.data, path) !== undefined) {
      if(this.debug) {
        if(path !== 'controller.clientModifiedTime') { // Spams the log
          return
        }

        const type = typeof state
        const typeCapitalized = type.charAt(0).toUpperCase() + type.slice(1, 3)

        if(state === undefined) {
          return
        } else if(type === 'object') {
          console.groupCollapsed(`%cSET Store ${path} to: (${typeCapitalized})`, this.storeColors.update)
          console.debug(state)
          console.groupEnd()
        } else {
          console.debug(`%cSET Store ${path} to: (${typeCapitalized}) ${state}`, this.storeColors.update)
        }
      }

      if(state !== undefined) {
        _.set(this.data, path, state)
      } else {
        console.warn(`%cREJECT SET ${path} value is ${state}`, this.storeColors.warn)
      }
    } else {
      console.warn(`%cREJECT SET ${path} has no default`, this.storeColors.warn)
    }
  }

  /**
  @FUNC: getData
  @PARAMS: String Path, Var State
  @DESC: Is used to get data from store. It streamlines the process and creates
  a controllable entry point.
  */
  getData(path, state) {
    const data = _.get(this.data, path)
    if(data !== undefined) {
      return data
    } else {
      console.warn(`%cINVALID GET ${path} does not exist`, this.storeColors.warn)
    }
  }

  /**
  @FUNC: getUser
  @PARAMS: none
  @DESC: GETs user if any, ELSE sets default. For example when going to
  devicelist you want to clear data, but keep user.
  */
  getUser(force) {
    if(this.data && this.data.user && force !== true) {
      return this.data.user
    } else {
      return {
        permid: '5',
        permissions: null,
        access: {
          ngwi: null,
          wheel: null,
          jTurf: null,
          programEdit: null,
          sync: null,
          virtualControl: null
        }
      }
    }
  }


  /**
  @FUNC: getApplicationData
  @PARAMS: none
  @DESC: GETs application data if any, ELSE sets default. For example when going to
  devicelist you want to clear data, but keep versions.
  */
  getApplicationData() {
    return this.data && this.data.application
    ? this.data.application
    : {
      versions: {
        client: null,
        nodeClientServer: null
      }
    }
  }

  /**
  @FUNC: resetUser
  @PARAMS: none
  @DESC: SETs default userdata. For example when logging out.
  */
  resetUser() {
    this.setData('user', this.getUser(true)) // forces getUser to return base Obj
  }

  /**
  @FUNC: getSettings
  @PARAMS: none
  @DESC: SETs settings. First time if will check localStorage for saved settings
  and apply those if possible, ELSE it will set defaults. Consecutively it will
  try to mainting those settings even through resets. This is to avoid re-check
  on data resets.
  */
  getSettings() {
    if(this.data && this.data.settings) { // If settings have already been defined use those
      return this.data.settings
    } else {
      let localStorageSettings =  localStorage.getItem('NGWI_SETTINGS', JSON.stringify(this.computedSettings))
      const defaults = {
        time: {
          use12HourClock: true,
          currentFormat: 'MMM Do YYYY, h:mm A', // Should probably not be in here, but in general store
          formats: {
            format24: 'Do MMM YYYY, H:mm',
            format12: 'MMM Do YYYY, h:mm A'
          }
        },
        internationalization: {
          selectedLocale: defaultLocale
        },
        stations: {
          showAdvancedTab: false
        },
        dashSort: null
      }

      let settings = defaults

      if(!_.isEmpty(localStorageSettings)) { // localstorage return can be odd so any empty'ish return gets checked
        localStorageSettings = JSON.parse(localStorageSettings)

        const locale = languages[localStorageSettings.internationalization.selectedLocale]

        localStorageSettings.internationalization.selectedLocale = locale
        ? localStorageSettings.internationalization.selectedLocale
        : defaultLocale

        console.log('selectedLocale', localStorageSettings.internationalization.selectedLocale)

        settings = this._deepSameKeysRemoveDiff({}, defaults, localStorageSettings) // Checks localstorage for keys that doesn't exist in defaults (deprecated)
        settings = Object.assign(defaults, localStorageSettings) // Extend defaults with localstorage settings
      }
      localStorage.setItem('NGWI_SETTINGS', JSON.stringify(settings))
      
      return settings
    }
  }

  /**
  @FUNC: resetStoreData
  @PARAMS: none
  @DESC: (re)Sets the store data to default values, for example when accessing
  the devicelist or logging out. Some key/pairs are attempted kept.
  */

  resetStoreData() {
    // if(this.data === undefined) {
    //   // console.debug(`%cresetStoreData() Init`, this.storeColors.update);
    // } else {
    //   console.debug(`%cresetStoreData()`, this.storeColors.update)
    // }

    this.data = {
      application: this.getApplicationData(),
      // Settings
      settings: this.getSettings(),
      // System Capabilities
      systemCapabilities: {},
      // Programs
      programs: [],
      capabilities: null,
      editProgramsLocked: false,
      // Stations
      stations: [],
      // Alarms
      alarms: {
        list: null,
        count: null,
        maximumUrgency: null,
        maximumActiveUrgency: null,
        maximumUrgencyState: null,
        maximumActiveUrgencyState: null
      },
      // ET & Rain
      dailyEt: null,
      dailyRain: null, // Set but unused
      hourlyRain: null, // Set but unused
      // Voltage
      current: null,
      voltage: null,
      // Flow
      currentFlow: null,
      systemCapacity: null,
      expectedFlow: null,
      // RainShutDown
      rainShutDownTime: null,
      rainShutDown: {
        time: null,
        clientModifiedTime: null,
        showRainShutDownSpinner: null,
      },
      // Moisture
      moisture: null,
      // Controller
      controller: {
        name: null,
        mode: null,
        group: null,
        time: null,
        deviceData: null,
        clientModifiedTime: null,
        clientModifiedDevTime: null,
        clientTimeoutTime: null,
        clientTimeoutStartTime: null
      },
      user: this.getUser(),
      waterConsumption: null
    }
  }
}
